import { Select } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { keys, useApi } from '../api';
import { useIsReseller } from '../lib/me';

/**
 * Searchable customer picker over the first 200 customers by name (the
 * list's page limit), or a reseller's own customers. '' is "no customer"; placeholder names that choice.
 */
export function CustomerSelect({
  label,
  value,
  onChange,
  placeholder,
  w,
  error,
  hideLabel,
}: {
  label: string;
  value: string;
  onChange: (id: string) => void;
  placeholder: string;
  w?: number;
  error?: string;
  /** Names the field for screen readers only, for a filter row. */
  hideLabel?: boolean;
}) {
  const api = useApi();
  // A reseller picks among its own customers only.
  const reseller = useIsReseller();
  const customers = useQuery({
    queryKey: reseller ? keys.partnerCustomers : keys.customerOptions,
    queryFn: (): Promise<{ items: Array<{ id: string; name: string }> }> =>
      reseller ? api.partner.customers.list() : api.customers.list({ limit: 200 }),
    staleTime: 60_000,
  });
  return (
    <Select
      label={hideLabel ? undefined : label}
      aria-label={hideLabel ? label : undefined}
      size={hideLabel ? 'sm' : undefined}
      placeholder={customers.isPending ? 'Loading…' : placeholder}
      searchable
      clearable
      w={w}
      maw="100%"
      data={(customers.data?.items ?? []).map((c) => ({ value: c.id, label: c.name }))}
      value={value || null}
      onChange={(v) => onChange(v ?? '')}
      nothingFoundMessage="No customer matches"
      error={error ?? (customers.error ? 'Couldn’t load customers' : undefined)}
    />
  );
}
